"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Measure = void 0;
const https = require("https");
const API_HOST = 'measure-mrfusion.herokuapp.com';
const API_VERSION = '/v2';
const API_PORT = 443;
/**
 * Measure API handler
 */
class Measure {
    /**
     * Creates an instance of Measure.
     */
    constructor(host = API_HOST, version = API_VERSION, port = API_PORT) {
        this.host = host;
        this.version = version;
        this.port = port;
        this.headers = {};
        this.setBaseOptions();
        this.setJsonHeader();
    }
    /**
     * Sets base options for requests.
     */
    setBaseOptions() {
        this.options = {
            hostname: this.host,
            port: this.port,
            headers: this.headers,
            basePath: this.version,
            method: 'GET',
        };
    }
    /**
     * Sets origin header for requests.
     * Note: browsers override this by default.
     * @param origin
     */
    setOrigin(origin) {
        this.headers['Origin'] = origin;
    }
    /**
     * Sets json header for requests.
     */
    setJsonHeader() {
        this.headers['Content-Type'] = 'application/json';
    }
    /**
     * Sets auth token for requests.
     * @param token
     */
    setAuthToken(token) {
        const invalidToken = typeof token !== 'string';
        if (invalidToken) {
            throw new Error('Invalid token!');
        }
        this.headers['Authorization'] = token;
    }
    /**
     * Gets auth token
     * @returns auth token
     */
    getAuthToken() {
        const token = this.headers['Authorization'];
        if (token === undefined)
            return '';
        return token.toString();
    }
    /**
     * Checks API response errors and throws an Excetion if one is found.
     * @param response
     */
    static guardApiError(response) {
        if (response === undefined || response === null)
            return;
        if ('error' in response) {
            throw new Error(response.error.message);
        }
    }
    /**
     * Checks the provided UUID is correct and throws an Excetion otherwise.
     * @param uuid
     */
    static guardInvalidUuid(uuid) {
        const uuidRegex = new RegExp(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i);
        if (uuidRegex.test(uuid))
            return;
        throw new Error('Invalid UUID');
    }
    /**
     * Authenticates and returns the authenticated user data.
     * @param username
     * @param password
     * @returns user object
     */
    authenticate(username, password, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            const invalidUsername = typeof username !== 'string';
            const invalidPassword = typeof password !== 'string';
            if (invalidUsername || invalidPassword) {
                throw new Error('Invalid credentials');
            }
            const endpoint = '/login';
            const body = {
                username,
                password,
            };
            const data = yield this.postRaw(endpoint, body, requestHandler);
            const { token, user } = data;
            this.setAuthToken(token);
            return user;
        });
    }
    // ===================================================================================================
    //    USER ENDPOINTS
    // ===================================================================================================
    /**
     * Gets the authenticated user data.
     * @param uuid
     * @returns user object
     */
    getUser(uuid, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            Measure.guardInvalidUuid(uuid);
            const endpoint = `/users/${uuid}`;
            return this.getRaw(endpoint, requestHandler);
        });
    }
    /**
     * Gets all users. Only Admins can use this method.
     * @returns user objects list
     */
    getUsers(requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            const endpoint = `/users`;
            return this.getRaw(endpoint, requestHandler);
        });
    }
    /**
     * Creates a new user. Only Admins can use this method.
     * @param body
     * @returns user object
     */
    postUser(body, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            const endpoint = `/users`;
            return this.postRaw(endpoint, body, requestHandler);
        });
    }
    /**
     * Updates an existing user. Only Admins can use this method.
     * @param uuid
     * @param body
     * @returns user object
     */
    putUser(uuid, body, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            Measure.guardInvalidUuid(uuid);
            const endpoint = `/users/${uuid}`;
            return this.putRaw(endpoint, body, requestHandler);
        });
    }
    /**
     * Deletes an existing user. Only Admins can use this method.
     * @param uuid
     * @returns user object
     */
    deleteUser(uuid, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            Measure.guardInvalidUuid(uuid);
            const endpoint = `/users/${uuid}`;
            return this.deleteRaw(endpoint, requestHandler);
        });
    }
    /**
     * Updates the specified user password.
     * @param body
     * @returns user object
     */
    putUserPasswordChange(body, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            const endpoint = `/users/password`;
            return this.putRaw(endpoint, body, requestHandler);
        });
    }
    /**
     * Gets the authenticated user data.
     * @returns user object
     */
    getMe(requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            const endpoint = '/users/me';
            return this.getRaw(endpoint, requestHandler);
        });
    }
    /**
     * Gets all user contacts.
     * @param userUuid
     * @returns user contacts objects list
     */
    getUserContacts(userUuid, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            Measure.guardInvalidUuid(userUuid);
            const endpoint = `/users/contacts?user=${userUuid}`;
            return this.getRaw(endpoint, requestHandler);
        });
    }
    /**
     * Subscribe a user contact to a hub in order to receive notifications.
     * @param contactUuid
     * @param hubUuid
     * @returns user contacts object
     */
    postUserContactsSubscribe(contactUuid, hubUuid, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            Measure.guardInvalidUuid(contactUuid);
            Measure.guardInvalidUuid(hubUuid);
            const endpoint = `/users/contacts/subscribe?contact=${contactUuid}&hub=${hubUuid}`;
            return this.postRaw(endpoint, requestHandler);
        });
    }
    /**
     * Unsubscribe a user contact to a hub in order to receive notifications.
     * @param contactUuid
     * @param hubUuid
     * @returns user contacts object
     */
    postUserContactsUnsubscribe(contactUuid, hubUuid, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            Measure.guardInvalidUuid(contactUuid);
            Measure.guardInvalidUuid(hubUuid);
            const endpoint = `/users/contacts/unsubscribe?contact=${contactUuid}&hub=${hubUuid}`;
            return this.postRaw(endpoint, requestHandler);
        });
    }
    // ===================================================================================================
    //    ORGANIZATION ENDPOINTS
    // ===================================================================================================
    /**
     * Gets the authenticated user organization data.
     * @param uuid
     * @returns organization object
     */
    getOrganization(uuid, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            Measure.guardInvalidUuid(uuid);
            const endpoint = `/organizations/${uuid}`;
            return this.getRaw(endpoint, requestHandler);
        });
    }
    /**
     * Gets all organizations. Only Admins can use this method.
     * @returns organization objects list
     */
    getOrganizations(requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            const endpoint = `/organizations`;
            return this.getRaw(endpoint, requestHandler);
        });
    }
    /**
     * Creates a new organization. Only Admins can use this method.
     * @param body
     * @returns organization object
     */
    postOrganization(body, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            const endpoint = `/organizations`;
            return this.postRaw(endpoint, body, requestHandler);
        });
    }
    /**
     * Updates an existing organization. Only Admins can use this method.
     * @param uuid
     * @param body
     * @returns organization object
     */
    putOrganization(uuid, body, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            Measure.guardInvalidUuid(uuid);
            const endpoint = `/organizations/${uuid}`;
            return this.putRaw(endpoint, body, requestHandler);
        });
    }
    /**
     * Deletes an existing organization. Only Admins can use this method.
     * @param uuid
     * @returns organization object
     */
    deleteOrganization(uuid, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            Measure.guardInvalidUuid(uuid);
            const endpoint = `/organizations/${uuid}`;
            return this.deleteRaw(endpoint, requestHandler);
        });
    }
    // ===================================================================================================
    //    HUB ENDPOINTS
    // ===================================================================================================
    /**
     * Gets the authenticated user hub data.
     * @param uuid
     * @returns hub object
     */
    getHub(uuid, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            Measure.guardInvalidUuid(uuid);
            const endpoint = `/hubs/${uuid}`;
            return this.getRaw(endpoint, requestHandler);
        });
    }
    /**
     * Gets the authenticated user hubs data.
     * @param organizationId
     * @returns hub objects list
     */
    getHubs(organizationId, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            Measure.guardInvalidUuid(organizationId);
            const endpoint = `/hubs?organization=${organizationId}`;
            return this.getRaw(endpoint, requestHandler);
        });
    }
    /**
     * Gets the hub reports.
     * @param uuid of the hub
     * @returns hub reports list
     */
    getHubReports(uuid, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            Measure.guardInvalidUuid(uuid);
            const endpoint = `/hubs/reports?hub=${uuid}`;
            return this.getRaw(endpoint, requestHandler);
        });
    }
    /**
     * Gets the hub contacts.
     * @param uuid of the hub
     * @returns hub contacts list
     */
    getHubContacts(uuid, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            Measure.guardInvalidUuid(uuid);
            const endpoint = `/users/contacts?hub=${uuid}`;
            return this.getRaw(endpoint, requestHandler);
        });
    }
    /**
     * Creates a new hub. Only Admins can use this method.
     * @param body
     * @returns hub object
     */
    postHub(body, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            const endpoint = `/hubs`;
            return this.postRaw(endpoint, body, requestHandler);
        });
    }
    /**
     * Updates an existing hub. Only Admins can use this method.
     * @param uuid
     * @param body
     * @returns hub object
     */
    putHub(uuid, body, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            Measure.guardInvalidUuid(uuid);
            const endpoint = `/hubs/${uuid}`;
            return this.putRaw(endpoint, body, requestHandler);
        });
    }
    /**
     * Deletes an existing hub. Only Admins can use this method.
     * @param uuid
     * @returns hub object
     */
    deleteHub(uuid, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            Measure.guardInvalidUuid(uuid);
            const endpoint = `/hubs/${uuid}`;
            return this.deleteRaw(endpoint, requestHandler);
        });
    }
    // ===================================================================================================
    //    DEVICE ENDPOINTS
    // ===================================================================================================
    /**
     * Gets the authenticated user device data.
     * @param uuid
     * @returns device object
     */
    getDevice(uuid, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            Measure.guardInvalidUuid(uuid);
            const endpoint = `/devices/${uuid}`;
            return this.getRaw(endpoint, requestHandler);
        });
    }
    /**
     * Gets the authenticated user devices data.
     * @param hubUuid
     * @returns device object
     */
    getDevices(hubUuid, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            Measure.guardInvalidUuid(hubUuid);
            const endpoint = `/devices?hub=${hubUuid}`;
            return this.getRaw(endpoint, requestHandler);
        });
    }
    /**
     * Gets a device readings.
     * @param uuid
     * @param startDate EPOCH in seconds
     * @param endDate EPOCH in seconds
     * @returns
     */
    getDeviceReadings(uuid, startDate, endDate, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            Measure.guardInvalidUuid(uuid);
            const endpoint = `/devices/messages/${uuid}?startDate=${startDate}&endDate=${endDate}`;
            return this.getRaw(endpoint, requestHandler);
        });
    }
    /**
     * Gets a device last reading.
     * @param uuid
     * @returns
     */
    getDeviceLastReading(uuid, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            Measure.guardInvalidUuid(uuid);
            const endpoint = `/devices/messages/last/${uuid}`;
            return this.getRaw(endpoint, requestHandler);
        });
    }
    /**
     * Creates a new device. Only Admins can use this method.
     * @param body
     * @returns device object
     */
    postDevice(body, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            const endpoint = `/devices`;
            return this.postRaw(endpoint, body, requestHandler);
        });
    }
    /**
    * Updates an existing device. Only Admins can use this method.
    * @param uuid
    * @param body
    * @returns device object
    */
    putDevice(uuid, body, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            Measure.guardInvalidUuid(uuid);
            const endpoint = `/devices/${uuid}`;
            return this.putRaw(endpoint, body, requestHandler);
        });
    }
    /**
    * Deletes an existing device. Only Admins can use this method.
    * @param uuid
    * @returns device object
    */
    deleteDevice(uuid, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            Measure.guardInvalidUuid(uuid);
            const endpoint = `/devices/${uuid}`;
            return this.deleteRaw(endpoint, requestHandler);
        });
    }
    /**
     * Gets a device signature. Only Admins can use this method.
     * @param uuid
     * @returns device object
     */
    getDeviceSignature(uuid, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            Measure.guardInvalidUuid(uuid);
            const endpoint = `/devices/signature/${uuid}`;
            return this.getRaw(endpoint, requestHandler);
        });
    }
    // ===================================================================================================
    //    SENSOR ENDPOINTS
    // ===================================================================================================
    /**
     * Gets the authenticated user sensor data.
     * @param uuid
     * @returns sensor object
     */
    getSensor(uuid, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            Measure.guardInvalidUuid(uuid);
            const endpoint = `/sensors/${uuid}`;
            return this.getRaw(endpoint, requestHandler);
        });
    }
    /**
     * Gets the authenticated user sensors data.
     * @param deviceUuid
     * @returns sensor object
     */
    getSensors(deviceUuid, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            Measure.guardInvalidUuid(deviceUuid);
            const endpoint = `/sensors?device=${deviceUuid}`;
            return this.getRaw(endpoint, requestHandler);
        });
    }
    /**
     * Creates a new sensor. Only Admins can use this method.
     * @param body
     * @returns sensor object
     */
    postSensor(body, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            const endpoint = `/sensors`;
            return this.postRaw(endpoint, body, requestHandler);
        });
    }
    /**
    * Updates an existing sensor. Only Admins can use this method.
    * @param uuid
    * @param body
    * @returns sensor object
    */
    putSensor(uuid, body, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            Measure.guardInvalidUuid(uuid);
            const endpoint = `/sensors/${uuid}`;
            return this.putRaw(endpoint, body, requestHandler);
        });
    }
    /**
    * Deletes an existing sensor. Only Admins can use this method.
    * @param uuid
    * @returns sensor object
    */
    deleteSensor(uuid, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            Measure.guardInvalidUuid(uuid);
            const endpoint = `/sensors/${uuid}`;
            return this.deleteRaw(endpoint, requestHandler);
        });
    }
    /**
     * Gets a message list for a given sensor.
     * @param uuid
     * @param startDate
     * @param endDate
     * @param [intervalMinutes]
     * @returns message list
     */
    getMessages(uuid, startDate, endDate, intervalMinutes, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            Measure.guardInvalidUuid(uuid);
            const invalidStartDate = typeof startDate !== 'number';
            const invalidEndDate = typeof endDate !== 'number';
            if (invalidStartDate || invalidEndDate) {
                throw new Error('Invalid date, should be an EPOCH in seconds.');
            }
            let endpoint = '/sensors/messages';
            endpoint += '?';
            endpoint += `sensor=${uuid}`;
            endpoint += `&startDate=${startDate}`;
            endpoint += `&endDate=${endDate}`;
            if (typeof intervalMinutes === 'number') {
                endpoint += `&intervalMinutes=${intervalMinutes}`;
            }
            return this.getRaw(endpoint, requestHandler);
        });
    }
    /**
     * Gets the last message sent for a given sensor.
     * @param uuid
     * @returns last message object
     */
    getLastMessage(uuid, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            Measure.guardInvalidUuid(uuid);
            const endpoint = `/sensors/messages/last?sensor=${uuid}`;
            return this.getRaw(endpoint, requestHandler);
        });
    }
    // ===================================================================================================
    //    SENSOR CALIBRATION ENDPOINTS
    // ===================================================================================================
    /**
     * Creates a new sensor calibration. Only Admins can use this method.
     * @param body
     * @returns sensor calibration object
     */
    postSensorCalibration(body, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            const endpoint = `/sensors/calibrations`;
            return this.postRaw(endpoint, body, requestHandler);
        });
    }
    /**
    * Updates an existing sensor calibration. Only Admins can use this method.
    * @param uuid
    * @param body
    * @returns sensor calibration object
    */
    putSensorCalibration(uuid, body, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            Measure.guardInvalidUuid(uuid);
            const endpoint = `/sensors/calibrations/${uuid}`;
            return this.putRaw(endpoint, body, requestHandler);
        });
    }
    /**
    * Deletes an existing sensor calibration. Only Admins can use this method.
    * @param uuid
    * @returns sensor calibration object
    */
    deleteSensorCalibration(uuid, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            Measure.guardInvalidUuid(uuid);
            const endpoint = `/sensors/calibrations/${uuid}`;
            return this.deleteRaw(endpoint, requestHandler);
        });
    }
    // ===================================================================================================
    //    RAW REQUEST ENDPOINTS
    // ===================================================================================================
    /**
     * Fabricates a GET request for the provided enpoint string
     * @param endpoint
     * @returns data object
     */
    getRaw(endpoint, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            const options = Object.assign(Object.assign({}, this.options), { method: 'GET', endpoint });
            const data = yield Measure.requestAsync(options, undefined, requestHandler);
            Measure.guardApiError(data);
            return data;
        });
    }
    /**
     * Fabricates a POST request for the provided endpoint and sends the body
     * @param endpoint
     * @param body
     * @returns data object
     */
    postRaw(endpoint, body, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            const options = Object.assign(Object.assign({}, this.options), { method: 'POST', endpoint });
            const data = yield Measure.requestAsync(options, body, requestHandler);
            Measure.guardApiError(data);
            return data;
        });
    }
    /**
     * Fabricates a PUT request for the provided endpoint and sends the body
     * @param endpoint
     * @param body
     * @returns data object
     */
    putRaw(endpoint, body, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            const options = Object.assign(Object.assign({}, this.options), { method: 'PUT', endpoint });
            const data = yield Measure.requestAsync(options, body, requestHandler);
            Measure.guardApiError(data);
            return data;
        });
    }
    /**
     * Fabricates a DELETE request for the provided endpoint
     * @param endpoint
     * @returns data object
     */
    deleteRaw(endpoint, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            const options = Object.assign(Object.assign({}, this.options), { method: 'DELETE', endpoint });
            const data = yield Measure.requestAsync(options, undefined, requestHandler);
            Measure.guardApiError(data);
            return data;
        });
    }
    /**
     * Private: Make a request and get the response data.
     * @param options
     * @param [body]
     * @returns generic object
     */
    static requestAsync(options, body, requestHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            const { basePath, endpoint } = options;
            const parsedOptions = Object.assign(Object.assign({}, options), { path: `${basePath}${endpoint}` });
            delete parsedOptions.basePath;
            delete parsedOptions.endpoint;
            const postData = JSON.stringify(body);
            return new Promise((resolve, reject) => {
                const request = https.request(parsedOptions, (response) => {
                    response.setEncoding('utf8');
                    let output = '';
                    response.on('data', (chunk) => {
                        output += chunk;
                    });
                    response.on('end', () => {
                        const jsonResponse = JSON.parse(output);
                        resolve(jsonResponse);
                    });
                });
                request.on('error', (error) => {
                    reject(error);
                });
                if (postData) {
                    request.write(postData);
                }
                request.end();
                if (requestHandler) {
                    requestHandler.setRequest(request);
                }
            });
        });
    }
}
exports.Measure = Measure;
